import { addDaysLocal } from "@/lib/bookingDates";
import type { PitchElectricityMarker } from "@/lib/fernausleseApi";
import { resolveGuestName } from "@/lib/guestDisplay";
import type { Booking, Pitch } from "@/types/camping";

export interface MapPoint {
  x: number;
  y: number;
}

export type PitchMapStatusKind = "free" | "occupied" | "blocked" | "inactive";

export type PitchTransitionHint =
  | "departs_today"
  | "arrives_today"
  | "departs_tomorrow"
  | "arrives_tomorrow";

export interface SitePlanPitchStatus {
  pitch_id: string;
  code: string;
  label: string | null;
  map_polygon: MapPoint[] | null;
  status_kind: PitchMapStatusKind;
  transition_hint?: PitchTransitionHint | null;
  booking_id: string | null;
  booking_status: string | null;
  booking_number: string | null;
  guest_name?: string | null;
  reasons: string[];
}

export interface SitePlanStatusResponse {
  reference_date: string;
  wet_blocking_active: boolean;
  badge_scale: string;
  pitches: SitePlanPitchStatus[];
}

export interface SitePlanSettings {
  image_data: string | null;
  badge_scale: string;
  wet_pitch_blocking_active?: boolean;
}

const BLOCKING_BOOKING_STATUSES = new Set([
  "requested",
  "reserved",
  "confirmed",
  "checked_in",
]);

/** Buchung blockiert den Stellplatz am Stichtag (Halbintervall [Anreise, Abreise)). */
export function bookingBlocksPitchOnDate(
  booking: { status: string; arrival_date: string; departure_date: string },
  referenceDate: string,
): boolean {
  if (!BLOCKING_BOOKING_STATUSES.has(booking.status)) return false;
  return booking.arrival_date <= referenceDate && booking.departure_date > referenceDate;
}

/** Gast physisch am Stellplatz (Check-in abgeschlossen, Check-out noch ausstehend). */
export function bookingPhysicallyOccupiesPitchOnDate(
  booking: { status: string; arrival_date: string; pitch_id?: string | null },
  referenceDate: string,
): boolean {
  if (booking.status !== "checked_in") return false;
  if (!booking.pitch_id) return false;
  return booking.arrival_date <= referenceDate;
}

function earliestBlockingOnDate(
  bookings: Booking[],
  pitchId: string,
  referenceDate: string,
): Booking | undefined {
  let best: Booking | undefined;
  for (const booking of bookings) {
    if (booking.pitch_id !== pitchId) continue;
    if (!bookingBlocksPitchOnDate(booking, referenceDate)) continue;
    if (!best || booking.arrival_date < best.arrival_date) {
      best = booking;
    }
  }
  return best;
}

function earliestPhysicallyOccupiedOnDate(
  bookings: Booking[],
  pitchId: string,
  referenceDate: string,
): Booking | undefined {
  let best: Booking | undefined;
  for (const booking of bookings) {
    if (booking.pitch_id !== pitchId) continue;
    if (!bookingPhysicallyOccupiesPitchOnDate(booking, referenceDate)) continue;
    if (!best || booking.arrival_date < best.arrival_date) {
      best = booking;
    }
  }
  return best;
}

/** Anzahl physisch belegter Stellplätze (nur eingecheckte Gäste). */
export function countPhysicallyOccupiedPitches(
  bookings: Booking[],
  referenceDate: string,
  eligiblePitchIds?: ReadonlySet<string>,
): number {
  const occupiedPitchIds = new Set<string>();
  for (const booking of bookings) {
    if (!booking.pitch_id) continue;
    if (eligiblePitchIds && !eligiblePitchIds.has(booking.pitch_id)) continue;
    if (!bookingPhysicallyOccupiesPitchOnDate(booking, referenceDate)) continue;
    occupiedPitchIds.add(booking.pitch_id);
  }
  return occupiedPitchIds.size;
}

/**
 * Erwartete Belegung am Stichtag: alle Tages-Anreisen eingecheckt, Abreisen pünktlich
 * ausgecheckt (Halbintervall [Anreise, Abreise) mit blockierenden Buchungsstatus).
 */
export function countExpectedOccupiedPitches(
  bookings: Booking[],
  referenceDate: string,
  eligiblePitchIds?: ReadonlySet<string>,
): number {
  const occupiedPitchIds = new Set<string>();
  for (const booking of bookings) {
    if (!booking.pitch_id) continue;
    if (eligiblePitchIds && !eligiblePitchIds.has(booking.pitch_id)) continue;
    if (!bookingBlocksPitchOnDate(booking, referenceDate)) continue;
    occupiedPitchIds.add(booking.pitch_id);
  }
  return occupiedPitchIds.size;
}

export function computePitchTransitionHint(
  statusKind: PitchMapStatusKind,
  referenceDate: string,
  todayBooking: Booking | undefined,
  tomorrowBooking: Booking | undefined,
  yesterdayBooking: Booking | undefined,
): PitchTransitionHint | null {
  if (statusKind === "inactive" || statusKind === "blocked") return null;
  const tomorrow = addDaysLocal(referenceDate, 1);
  if (statusKind === "occupied" && todayBooking) {
    if (todayBooking.departure_date === tomorrow) return "departs_tomorrow";
    if (todayBooking.arrival_date === referenceDate) return "arrives_today";
    return null;
  }
  if (statusKind === "free") {
    if (yesterdayBooking && !todayBooking) return "departs_today";
    if (tomorrowBooking) return "arrives_tomorrow";
  }
  return null;
}

/**
 * Fallback, wenn GET /api/v1/site-plan/status nicht erreichbar ist (z. B. ältere API).
 */
export function computeSitePlanStatusLocal(
  pitches: Pitch[],
  bookings: Booking[],
  referenceDate: string,
  options: { wetBlockingActive?: boolean; badgeScale?: string } = {},
): SitePlanStatusResponse {
  const wetBlockingActive = options.wetBlockingActive ?? false;
  const badgeScale = options.badgeScale ?? String(DEFAULT_BADGE_SCALE);

  const tomorrow = addDaysLocal(referenceDate, 1);
  const yesterday = addDaysLocal(referenceDate, -1);

  const items: SitePlanPitchStatus[] = pitches.map((pitch) => {
    const todayBooking = earliestPhysicallyOccupiedOnDate(bookings, pitch.id, referenceDate);
    const tomorrowBooking = earliestBlockingOnDate(bookings, pitch.id, tomorrow);
    const yesterdayBooking = earliestBlockingOnDate(bookings, pitch.id, yesterday);
    let statusKind: PitchMapStatusKind = "free";
    const reasons: string[] = [];

    if (!pitch.is_active) {
      statusKind = "inactive";
      reasons.push("Stellplatz ist inaktiv.");
    } else if (pitch.is_blocked) {
      statusKind = "blocked";
      reasons.push("Stellplatz ist gesperrt.");
    } else if (wetBlockingActive && pitch.blocked_when_wet) {
      statusKind = "blocked";
      reasons.push("Stellplatz ist bei Nässe gesperrt.");
    } else if (todayBooking) {
      statusKind = "occupied";
    }

    const transitionHint = computePitchTransitionHint(
      statusKind,
      referenceDate,
      todayBooking,
      tomorrowBooking,
      yesterdayBooking,
    );

    return {
      pitch_id: pitch.id,
      code: pitch.code,
      label: pitch.label,
      map_polygon: pitch.map_polygon ?? null,
      status_kind: statusKind,
      transition_hint: transitionHint,
      booking_id: todayBooking?.id ?? null,
      booking_status: todayBooking?.status ?? null,
      booking_number: todayBooking?.booking_number ?? null,
      reasons,
    };
  });

  items.sort((a, b) => a.code.localeCompare(b.code, "de"));

  return {
    reference_date: referenceDate,
    wet_blocking_active: wetBlockingActive,
    badge_scale: badgeScale,
    pitches: items,
  };
}

export const DEFAULT_BADGE_SCALE = 1;
export const MIN_BADGE_SCALE = 0.15;
export const MAX_BADGE_SCALE = 2.5;
export const BASE_BADGE_PX = 32;

/** Client-seitiger Zoom des Platzplans (nicht persistiert). */
export const MIN_PLAN_ZOOM = 0.5;
export const MAX_PLAN_ZOOM = 12;
export const PLAN_ZOOM_STEP = 0.25;

/** Eckpunkt-Marker relativ zur Badge-Größe (20px bei 32px Badge). */
const CORNER_MARKER_BADGE_RATIO = 0.625;
const BADGE_FONT_SIZE_RATIO = 0.38;
const BADGE_FONT_SIZE_MIN_RATIO = 0.16;
const BADGE_CHAR_WIDTH_RATIO = 0.58;
const CORNER_MARKER_FONT_RATIO = 0.5;

export function clampBadgeScale(scale: number): number {
  return Math.min(MAX_BADGE_SCALE, Math.max(MIN_BADGE_SCALE, scale));
}

export function badgeSizePx(scale: number): number {
  return Math.round(BASE_BADGE_PX * clampBadgeScale(scale));
}

export function badgeFontSizePx(badgePx: number): number {
  return Math.max(4, Math.round(badgePx * BADGE_FONT_SIZE_RATIO));
}

/** Schriftgröße so wählen, dass der Platzcode bei gegebener Badge-Größe vollständig lesbar ist. */
export function badgeFontSizeForCode(code: string, badgePx: number): number {
  const maxFont = badgeFontSizePx(badgePx);
  const minFont = Math.max(4, Math.round(badgePx * BADGE_FONT_SIZE_MIN_RATIO));
  const chars = Math.max(1, code.trim().length);
  const availableWidth = badgePx * 0.9;
  const widthPerEm = BADGE_CHAR_WIDTH_RATIO;
  const fitted = Math.floor(availableWidth / (chars * widthPerEm));
  return Math.max(minFont, Math.min(maxFont, fitted));
}

export function cornerMarkerSizePx(badgePx: number): number {
  return Math.max(3, Math.round(badgePx * CORNER_MARKER_BADGE_RATIO));
}

export function cornerMarkerFontSizePx(markerPx: number): number {
  return Math.max(4, Math.round(markerPx * CORNER_MARKER_FONT_RATIO));
}

export function clampPlanZoom(zoom: number): number {
  return Math.min(MAX_PLAN_ZOOM, Math.max(MIN_PLAN_ZOOM, zoom));
}

/** Schwerpunkt eines Polygons (normalisierte Koordinaten). */
export function polygonCentroid(points: MapPoint[]): MapPoint {
  if (points.length === 0) return { x: 0.5, y: 0.5 };
  const first = points[0];
  if (!first) return { x: 0.5, y: 0.5 };
  if (points.length === 1) return { ...first };
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i += 1) {
    const j = (i + 1) % points.length;
    const pi = points[i]!;
    const pj = points[j]!;
    const cross = pi.x * pj.y - pj.x * pi.y;
    area += cross;
    cx += (pi.x + pj.x) * cross;
    cy += (pi.y + pj.y) * cross;
  }
  area *= 0.5;
  if (Math.abs(area) < 1e-8) {
    const avgX = points.reduce((s, p) => s + p.x, 0) / points.length;
    const avgY = points.reduce((s, p) => s + p.y, 0) / points.length;
    return { x: avgX, y: avgY };
  }
  return { x: cx / (6 * area), y: cy / (6 * area) };
}

function pointInPolygon(point: MapPoint, polygon: MapPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    const crosses =
      pi.y > point.y !== pj.y > point.y &&
      point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Punkt im oberen rechten Bereich der Fläche.
 * Ausgang ist die Ecke des Polygons, nicht die Ecke der Bounding-Box:
 * bei schrägen Plätzen liegt die Box-Ecke neben der Fläche.
 */
export function polygonTopRightInset(points: MapPoint[]): MapPoint {
  const centroid = polygonCentroid(points);
  const first = points[0];
  if (!first || points.length < 3) return centroid;

  let minX = first.x;
  let maxX = first.x;
  let minY = first.y;
  let maxY = first.y;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  const width = Math.max(maxX - minX, 1e-6);
  const height = Math.max(maxY - minY, 1e-6);

  let corner = first;
  let bestDist = Infinity;
  for (const point of points) {
    const dx = (maxX - point.x) / width;
    const dy = (point.y - minY) / height;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      corner = point;
    }
  }

  // Etwa ein Viertel der Fläche von der Ecke nach innen, damit das Badge
  // auf dem Platz liegt und nicht auf dem Rand oder in der Lücke daneben.
  let t = 0.55;
  let placed = {
    x: corner.x + (centroid.x - corner.x) * t,
    y: corner.y + (centroid.y - corner.y) * t,
  };
  while (t < 0.9 && !pointInPolygon(placed, points)) {
    t += 0.08;
    placed = {
      x: corner.x + (centroid.x - corner.x) * t,
      y: corner.y + (centroid.y - corner.y) * t,
    };
  }
  return placed;
}

export function pointsToSvg(points: MapPoint[]): string {
  return points.map((p) => `${p.x},${p.y}`).join(" ");
}

/** Normalisierter Eckradius (0–1) für abgerundete Platzflächen auf dem SVG-Plan. */
export const SITE_PLAN_PITCH_CORNER_RADIUS = 0.007;

/** Erzeugt einen SVG-Pfad mit leicht abgerundeten Ecken für Glaskachel-Platzflächen. */
export function polygonToRoundedPath(
  points: MapPoint[],
  radius = SITE_PLAN_PITCH_CORNER_RADIUS,
): string {
  const n = points.length;
  if (n < 3) return "";

  const parts: string[] = [];

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n]!;
    const curr = points[i]!;
    const next = points[(i + 1) % n]!;

    const toPrevX = prev.x - curr.x;
    const toPrevY = prev.y - curr.y;
    const toNextX = next.x - curr.x;
    const toNextY = next.y - curr.y;

    const lenPrev = Math.hypot(toPrevX, toPrevY);
    const lenNext = Math.hypot(toNextX, toNextY);
    if (lenPrev < 1e-10 || lenNext < 1e-10) continue;

    const cut = Math.min(radius, lenPrev / 2, lenNext / 2);

    const startX = curr.x + (toPrevX / lenPrev) * cut;
    const startY = curr.y + (toPrevY / lenPrev) * cut;
    const endX = curr.x + (toNextX / lenNext) * cut;
    const endY = curr.y + (toNextY / lenNext) * cut;

    if (i === 0) {
      parts.push(`M ${startX},${startY}`);
    } else {
      parts.push(`L ${startX},${startY}`);
    }
    parts.push(`Q ${curr.x},${curr.y} ${endX},${endY}`);
  }

  parts.push("Z");
  return parts.join(" ");
}

/** Semantische Akzentfarbe für Rahmen, Glow und dezente Flächenfüllung. */
export type SitePlanPitchAccent =
  | "green"
  | "blue"
  | "amber"
  | "orange"
  | "red"
  | "teal"
  | "accent"
  | "violet"
  | "neutral";

export interface SitePlanPitchSurface {
  accent: SitePlanPitchAccent;
  /** CSS-Klasse für die Polygon-Fläche (site-plan-pitch--*). */
  surfaceClass: string;
}

const BOOKING_ACCENT: Record<string, SitePlanPitchAccent> = {
  draft: "neutral",
  requested: "orange",
  reserved: "amber",
  confirmed: "red",
  checked_in: "teal",
  checked_out: "neutral",
  cancelled: "red",
  rejected: "red",
  no_show: "red",
};

const TRANSITION_ACCENT: Record<PitchTransitionHint, SitePlanPitchAccent> = {
  departs_today: "blue",
  arrives_tomorrow: "amber",
  departs_tomorrow: "blue",
  arrives_today: "amber",
};

function pitchAccentToSurfaceClass(accent: SitePlanPitchAccent): string {
  return `site-plan-pitch--${accent}`;
}

/** Bestimmt Akzent und CSS-Klassen für die Glas-Platzfläche. */
export type PitchHighlightRole = "none" | "candidate" | "assigned" | "dimmed";

export function sitePlanPitchSurface(
  statusKind: PitchMapStatusKind,
  bookingStatus?: string | null,
  transitionHint?: PitchTransitionHint | null,
  options: { selected?: boolean; highlightRole?: PitchHighlightRole } = {},
): SitePlanPitchSurface {
  if (options.highlightRole === "dimmed") {
    return {
      accent: "neutral",
      surfaceClass: "site-plan-pitch--neutral site-plan-pitch--dimmed-polygon",
    };
  }
  if (options.highlightRole === "candidate") {
    return {
      accent: "amber",
      surfaceClass: "site-plan-pitch--amber site-plan-pitch--candidate",
    };
  }
  if (options.highlightRole === "assigned" || options.selected) {
    return {
      accent: "accent",
      surfaceClass: "site-plan-pitch--accent site-plan-pitch--selected",
    };
  }

  if (statusKind === "inactive") {
    return { accent: "neutral", surfaceClass: pitchAccentToSurfaceClass("neutral") };
  }
  if (statusKind === "blocked") {
    return { accent: "violet", surfaceClass: pitchAccentToSurfaceClass("violet") };
  }
  if (statusKind === "free") {
    if (transitionHint === "departs_today") {
      return { accent: "blue", surfaceClass: pitchAccentToSurfaceClass("blue") };
    }
    if (transitionHint === "arrives_tomorrow") {
      return { accent: "amber", surfaceClass: pitchAccentToSurfaceClass("amber") };
    }
    return { accent: "green", surfaceClass: pitchAccentToSurfaceClass("green") };
  }

  if (transitionHint && statusKind === "occupied") {
    const transitionAccent = TRANSITION_ACCENT[transitionHint];
    if (transitionHint === "departs_tomorrow") {
      return {
        accent: transitionAccent,
        surfaceClass: pitchAccentToSurfaceClass(transitionAccent),
      };
    }
    if (transitionHint === "arrives_today") {
      return {
        accent: transitionAccent,
        surfaceClass: pitchAccentToSurfaceClass(transitionAccent),
      };
    }
  }

  const key = (bookingStatus ?? "").toLowerCase().replace(/\s+/g, "_");
  const accent = BOOKING_ACCENT[key] ?? "red";
  return { accent, surfaceClass: pitchAccentToSurfaceClass(accent) };
}

/** @deprecated Nur für Abwärtskompatibilität in Tests – nutze sitePlanPitchSurface. */
export function sitePlanBadgeClasses(
  statusKind: PitchMapStatusKind,
  bookingStatus?: string | null,
  transitionHint?: PitchTransitionHint | null,
): string {
  return sitePlanPitchSurface(statusKind, bookingStatus, transitionHint).surfaceClass;
}

export function transitionHintLabel(hint: PitchTransitionHint): string {
  switch (hint) {
    case "departs_today":
      return "Heute frei (Abreise)";
    case "arrives_tomorrow":
      return "Ab morgen belegt";
    case "departs_tomorrow":
      return "Abreise morgen";
    case "arrives_today":
      return "Anreise heute";
  }
}

export const SITE_PLAN_LEGEND: {
  label: string;
  accent: SitePlanPitchAccent;
}[] = [
  { label: "Frei", accent: "green" },
  { label: "Heute frei (Abreise)", accent: "blue" },
  { label: "Ab morgen belegt", accent: "amber" },
  { label: "Belegt (bestätigt)", accent: "red" },
  { label: "Belegt (eingecheckt)", accent: "teal" },
  { label: "Belegt (angefragt)", accent: "orange" },
  { label: "Abreise morgen", accent: "blue" },
  { label: "Gesperrt", accent: "violet" },
  { label: "Inaktiv", accent: "neutral" },
];

export function statusKindLabel(
  kind: PitchMapStatusKind,
  transitionHint?: PitchTransitionHint | null,
): string {
  if (transitionHint) {
    return transitionHintLabel(transitionHint);
  }
  switch (kind) {
    case "free":
      return "Frei";
    case "occupied":
      return "Belegt";
    case "blocked":
      return "Gesperrt";
    case "inactive":
      return "Inaktiv";
  }
}

/** Overlay-Felder für SitePlanCanvas inkl. Hover-Tooltip (Status, Gast bei Belegung). */
export interface SitePlanPitchOverlay {
  id: string;
  code: string;
  map_polygon: MapPoint[] | null;
  status_kind?: PitchMapStatusKind;
  transition_hint?: PitchTransitionHint | null;
  booking_status?: string | null;
  status_label?: string;
  guest_name?: string | null;
  /** Bezeichnung des Platzes, falls sie vom Kürzel abweicht. */
  label?: string | null;
  booking_number?: string | null;
  /** Gründe, warum der Platz am Stichtag nicht frei ist. */
  reasons?: string[];
  /** Strom am Platz: an, aus oder Versorgungsfehler. Fehlt ohne Anschluss. */
  electricity_status?: PitchElectricityMarker | null;
}

export function buildSitePlanPitchOverlays(
  pitches: SitePlanPitchStatus[],
  bookings?: Booking[],
): SitePlanPitchOverlay[] {
  const bookingById = bookings ? new Map(bookings.map((b) => [b.id, b])) : null;

  return pitches.map((p) => {
    const booking =
      p.booking_id && bookingById ? bookingById.get(p.booking_id) : undefined;
    let guestName: string | null = p.guest_name ?? null;
    if (!guestName && p.status_kind === "occupied" && booking) {
      guestName = resolveGuestName(booking.guest_id, { booking });
    }

    return {
      id: p.pitch_id,
      code: p.code,
      map_polygon: p.map_polygon,
      status_kind: p.status_kind,
      transition_hint: p.transition_hint,
      booking_status: p.booking_status,
      status_label: statusKindLabel(p.status_kind, p.transition_hint),
      guest_name: guestName,
      label: p.label,
      booking_number: p.booking_number,
      reasons: p.reasons ?? [],
    };
  });
}

export function readImageFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Datei konnte nicht gelesen werden."));
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}
